import { useState } from 'react';
import type { SurveyDto } from '@zoxo/shared';

import { PILL_TAB, PILL_TAB_OFF, PILL_TAB_ON } from '../../components/ui/pill-tab';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import { signInWithKakao } from '../../lib/supabase';
import { useVote } from './use-posts';

/**
 * 글에 붙은 설문 — 보기와 투표. 결과(표 수)는 서버가 투표한 뒤(또는 마감 뒤)에만 준다(FOMO 게이트).
 * 단일 선택은 누르면 바로 보내고, 복수 선택은 골라 두었다가 「투표」로 보낸다.
 */
export function SurveyVote({ postId, survey }: { postId: string; survey: SurveyDto }) {
  const { isAuthenticated } = useAuth();
  const vote = useVote(postId);
  const [picked, setPicked] = useState<string[]>(survey.myOptionIds ?? []);
  const unlocked = survey.hasVoted || survey.isClosed;
  const total = survey.totalVotes;

  const submit = (ids: string[]) => {
    if (!isAuthenticated) {
      void signInWithKakao();
      return;
    }
    if (ids.length === 0) return;
    vote.mutate({ optionIds: ids });
  };
  const toggle = (id: string) => {
    if (survey.isClosed) return;
    if (survey.answerType === 'single') {
      setPicked([id]);
      submit([id]);
      return;
    }
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <section aria-label="설문" className="mt-8 border-t border-[#e3d8bf] pt-8">
      {/* 안내문 — 본문과 같은 명조체 14px. */}
      <div className="tiptap-serif mb-4 flex items-center justify-between gap-2 text-sm text-text-muted">
        <span>{survey.answerType === 'single' ? '설문조사) 다음 중 나의 답변을 한개 고른다면?' : '설문조사) 다음 중 나의 답변을 모두 고른다면?'}</span>
        <span className="tabular-nums">{total.toLocaleString('ko-KR')}명 참여{survey.isClosed ? ' · 마감' : ''}</span>
      </div>
      <ul className="space-y-3">
        {survey.options.map((o) => {
          const mine = picked.includes(o.id);
          const pct = unlocked && total > 0 ? Math.round(((o.voteCount ?? 0) / total) * 100) : null;
          return (
            <li key={o.id}>
              <button
                type="button"
                role={survey.answerType === 'single' ? 'radio' : 'checkbox'}
                aria-checked={mine}
                disabled={survey.isClosed || vote.isPending}
                onClick={() => toggle(o.id)}
                // 버튼답게: 흰 바탕·뚜렷한 테두리·그림자·44px 높이. 고른 것은 검정 테두리 두 겹.
                className={cn(
                  'relative flex min-h-11 w-full items-center justify-between overflow-hidden rounded-lg border bg-white px-4 py-2.5 text-left text-[15px] font-medium shadow-sm transition-all disabled:cursor-default',
                  mine
                    ? 'border-gray-900 text-gray-900 ring-1 ring-gray-900'
                    : 'border-gray-300 text-gray-800 hover:border-gray-900 hover:shadow active:translate-y-px',
                )}
              >
                {pct !== null ? (
                  <span aria-hidden className="absolute inset-y-0 left-0 bg-brand-soft" style={{ width: `${pct}%` }} />
                ) : null}
                <span className="relative">{o.label}</span>
                {pct !== null ? <span className="relative tabular-nums text-brand-strong">{pct}%</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
      {survey.answerType === 'multiple' && !survey.isClosed ? (
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={() => submit(picked)}
            disabled={picked.length === 0 || vote.isPending}
            className={cn(PILL_TAB, picked.length === 0 ? cn(PILL_TAB_OFF, 'border border-gray-200 bg-white') : PILL_TAB_ON, 'disabled:opacity-50')}
          >
            {vote.isPending ? '보내는 중…' : survey.hasVoted ? '다시 투표' : '투표'}
          </button>
        </div>
      ) : null}
      {vote.isError ? <p className="mt-2 text-xs text-danger" role="alert">투표를 보내지 못했어요. 잠시 후 다시 시도해 주세요.</p> : null}
    </section>
  );
}
